import { Spin } from "antd";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExclamationCircleOutlined, MenuOutlined } from "@ant-design/icons";
import ProfileDropdown from "../../../../components/ProfileDropdown";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { logout, setAccess } from "../../../../store/authSlice";
import { denyAccess } from "../../../../api/access";
import type { ConfigOutletContext } from "../../Configuration";
import { useCompanies } from "../../../../hooks/useCompanies";
import CompaniesTable from "./CompaniesTable";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  ink: "var(--color-ink)",
  textSoft: "var(--color-body)",
  brandTint: "var(--color-brand-tint)",
  danger: "var(--color-danger-ink)",
};

const CompaniesPage = () => {
  const { t } = useTranslation();
  const { isNarrow, onOpenMobileMenu } = useOutletContext<ConfigOutletContext>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const access = useAppSelector((s) => s.auth.access);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const displayName = names || username || "";

  const { data: companies = [], isLoading, isError } = useCompanies();

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ background: R.bg }}>
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
          gap: 10,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1 }}
        >
          {isNarrow && (
            <button
              onClick={onOpenMobileMenu}
              aria-label="Open menu"
              style={{
                height: 34,
                width: 34,
                borderRadius: 8,
                background: "transparent",
                border: "none",
                color: R.ink,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
          )}
          <h1
            style={{
              fontSize: isNarrow ? 18 : 22,
              fontWeight: 700,
              letterSpacing: -0.5,
              margin: 0,
              color: R.ink,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("companiesConfig.title")}
          </h1>
        </div>

        <div
          className="flex items-center"
          style={{ gap: isNarrow ? 6 : 10, flexShrink: 0 }}
        >
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ padding: isNarrow ? "14px 14px 24px" : "22px 28px 32px" }}
      >
        <div className="flex flex-col" style={{ gap: 14 }}>
          {isLoading ? (
            <div
              style={{
                padding: 48,
                display: "flex",
                justifyContent: "center",
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 14,
              }}
            >
              <Spin size="large" />
            </div>
          ) : isError ? (
            <div
              style={{
                padding: "22px 24px",
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 14,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "var(--color-danger-tint)",
                  color: R.danger,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 16 }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: R.ink,
                    letterSpacing: -0.2,
                  }}
                >
                  {t("companiesConfig.loadingError")}
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: R.textSoft,
                    marginTop: 2,
                  }}
                >
                  {t("companiesConfig.loadingErrorDesc")}
                </div>
              </div>
            </div>
          ) : (
            <CompaniesTable companies={companies} isNarrow={isNarrow} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CompaniesPage;
